import {Box, Button, ButtonProps} from '@miro-site/core'
import {transparentize} from 'polished'
import React, {useState, useEffect} from 'react'
import styled, {css} from 'styled-components'
import TransparentButton from '../../button/TransparentButton'
import SecondaryLink from '../../link/SecondaryLink'
import {TemplateActions} from '../templateActions'

export interface ActionsOverlayProps {
  className?: string
  templateActions: TemplateActions
  previewButton?: (defaultButton: React.ReactElement) => React.ReactElement
  actionButtonProps?: Partial<ButtonProps>
  onMoreInfoClick: React.MouseEventHandler<HTMLElement>
  boardId: string
  templatePagePath: string
  isVisible: boolean
}

const ActionsOverlayBase: React.FC<ActionsOverlayProps> = ({
  boardId,
  templateActions,
  templatePagePath,
  className,
  onMoreInfoClick,
  previewButton,
  actionButtonProps,
  isVisible,
}) => {
  const defaultPreviewButton = (
    <TransparentButton
      $size="small"
      href={templatePagePath}
      onFocus={() => setOverlayVisible(true)}
      onBlur={() => setOverlayVisible(false)}
    >
      {templateActions.PREVIEW}
    </TransparentButton>
  )

  const [isOverlayVisible, setOverlayVisible] = useState(isVisible)

  useEffect(() => {
    setOverlayVisible(isVisible)
  }, [isVisible])

  return (
    <Box
      data-track-place="actions-overlay"
      className={className}
      $styles={{
        position: 'absolute',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: 2,
        background: 'primaryWhite',
        borderRadius: [8, 16, 16, 16],
        transition: 'opacity 250ms, visibility 250ms',
        opacity: isOverlayVisible ? 1 : 0,
        pointerEvents: isOverlayVisible ? 'all' : 'none',
      }}
    >
      <Box
        $styles={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {actionButtonProps ? (
          <Button
            data-track-id="use-template"
            data-template-id={boardId}
            $buttonType="primary"
            $size="small"
            $mr={12}
            onFocus={() => setOverlayVisible(true)}
            onBlur={() => setOverlayVisible(false)}
            {...actionButtonProps}
          >
            {actionButtonProps.children || templateActions.USE_TEMPLATE}
          </Button>
        ) : null}
        {previewButton ? previewButton(defaultPreviewButton) : defaultPreviewButton}
        <SecondaryLink
          $isDashed={true}
          onClick={onMoreInfoClick}
          $styles={{
            position: 'absolute',
            right: 20,
            bottom: 25,
          }}
          data-testid="more-info-button"
          onFocus={() => setOverlayVisible(true)}
          onBlur={() => setOverlayVisible(false)}
        >
          {templateActions.SHOW_MORE_INFO}
        </SecondaryLink>
      </Box>
    </Box>
  )
}

const ActionsOverlay = styled(ActionsOverlayBase)(
  ({theme}) => css`
    background: ${transparentize(0.05, theme.colors.primaryWhite)};
    border: 1px solid ${theme.colors.grey};
    box-shadow: ${theme.templates.elevation.overlay};
  `,
)

export default ActionsOverlay
